import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Copy, Crown, Trash, Users, Share, SignOut, At } from "@phosphor-icons/react";
import TeamBadge from "../components/TeamBadge";
import PremierBadge from "../components/PremierBadge";
import RankCell from "../components/RankCell";

export default function LeagueDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/leagues/${id}/leaderboard`);
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load league");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const copyCode = () => {
    if (data?.league) {
      navigator.clipboard.writeText(data.league.invite_code);
      toast.success("Invite code copied");
    }
  };

  const shareLink = () => {
    if (!data?.league) return;
    const url = `${window.location.origin}/leagues?code=${data.league.invite_code}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied");
  };

  const leaveLeague = async () => {
    if (!data?.league) return;
    if (!window.confirm(`Leave "${data.league.name}"? You can rejoin with the invite code.`)) return;
    try {
      await apiClient.post(`/leagues/${id}/leave`);
      toast.success("Left league");
      window.location.href = "/leagues";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const delLeague = async () => {
    if (!window.confirm("Delete this league? Members will lose access.")) return;
    try {
      await apiClient.delete(`/leagues/${id}`);
      toast.success("League deleted");
      window.location.href = "/leagues";
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12 text-white/50 uppercase tracking-widest">Loading…</div>;
  if (!data) return null;
  const { league, leaderboard } = data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link data-testid="league-back" to="/leagues" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/50 hover:text-primary-yellow">
        <ArrowLeft size={12} /> All leagues
      </Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Private League</span>
          <h1 data-testid="league-title" className="font-heading text-5xl uppercase font-black mt-2 leading-tight">{league.name}</h1>
          <div className="mt-2 flex items-center gap-3 text-xs text-white/60">
            <span className="flex items-center gap-1"><Users size={12} /> {league.member_count} members</span>
            <span>·</span>
            <span>renews {new Date(league.expires_at).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="league-copy-code"
            onClick={copyCode}
            className="inline-flex items-center gap-2 border border-white/10 px-4 py-3 hover:border-primary-yellow hover:text-primary-yellow transition text-xs font-mono-num tracking-widest"
          >
            <Copy size={12} weight="bold" /> {league.invite_code}
          </button>
          <button
            data-testid="league-share-link"
            onClick={shareLink}
            className="inline-flex items-center gap-2 border border-white/10 px-4 py-3 hover:border-primary-yellow hover:text-primary-yellow transition text-xs uppercase tracking-widest"
          >
            <Share size={12} weight="bold" /> Share link
          </button>
          {!league.is_creator && (
            <button
              data-testid="league-leave"
              onClick={leaveLeague}
              className="inline-flex items-center gap-2 border border-red-500/30 text-red-300 hover:bg-red-500/10 px-4 py-3 text-xs uppercase tracking-widest"
            >
              <SignOut size={12} weight="bold" /> Leave
            </button>
          )}
          {league.is_creator && (
            <button
              data-testid="league-delete"
              onClick={delLeague}
              className="border border-red-500/40 text-red-400 hover:bg-red-500/10 p-3"
              aria-label="delete league"
            >
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="mt-10 border border-white/10">
        <div className="grid grid-cols-12 gap-2 px-4 sm:px-6 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-white/40">
          <div className="col-span-2">Rank</div>
          <div className="col-span-4">Predictor</div>
          <div className="col-span-2 text-right">Picks</div>
          <div className="col-span-2 text-right">Exact</div>
          <div className="col-span-2 text-right">Points</div>
        </div>
        {leaderboard.length === 0 && (
          <div className="p-10 text-white/60 text-center">No members yet.</div>
        )}
        {leaderboard.map((r) => {
          const isMe = user?.id === r.user_id;
          const displayName = r.username || r.name || "Anonymous";
          return (
            <div
              key={r.user_id}
              data-testid={`league-lb-${r.rank}`}
              className={`grid grid-cols-12 gap-2 px-4 sm:px-6 py-4 border-b border-white/5 last:border-b-0 surface items-center ${isMe ? "bg-primary-yellow/5 border-l-2 border-l-primary-yellow" : ""}`}
            >
              <div className="col-span-2">
                <RankCell
                  rank={r.rank}
                  previousRank={r.previous_rank}
                  bestRank={r.best_rank}
                />
              </div>
              <div className="col-span-4 flex items-center gap-3">
                <div className="w-8 h-8 surface-elevated border border-white/10 grid place-items-center font-heading font-black uppercase text-sm">
                  {displayName[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold uppercase tracking-wide text-sm flex items-center gap-1.5">
                    <At size={11} className="text-white/40" />
                    <span className="font-mono-num">{displayName}</span>
                    <TeamBadge team={r.favourite_team} />
                    <PremierBadge active={r.is_premier} />
                    {isMe && <span className="ml-2 text-[10px] text-primary-yellow uppercase">· You</span>}
                  </div>
                  {r.name && r.name !== r.username && (
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{r.name}</div>
                  )}
                  {r.is_pro && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-blue-300 uppercase tracking-widest mt-1">
                      <Crown size={10} weight="fill" /> Premier
                    </span>
                  )}
                </div>
              </div>
              <div className="col-span-2 text-right font-mono-num">{r.predictions}</div>
              <div className="col-span-2 text-right font-mono-num text-primary-yellow">{r.exact_scores}</div>
              <div className="col-span-2 text-right font-mono-num text-2xl font-black text-primary-yellow">{r.total_points}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
